import { useEffect, useState } from "react";
import { usePage } from "@inertiajs/react";
import clsx from "clsx";
import type { SharedProps } from "@/types";

// Flash used to be buried in the sidebar, where it was easy to miss on mobile.
// It now floats above the content and can be dismissed.
export default function FlashStack() {
  const { flash } = usePage<SharedProps>();
  const [dismissed, setDismissed] = useState<string[]>([]);

  const entries = Object.entries(flash ?? {}).filter(
    ([key, message]) => message && !dismissed.includes(key),
  );

  // A new flash after a visit should reappear even if its key was dismissed.
  useEffect(() => {
    setDismissed([]);
  }, [flash]);

  if (entries.length === 0) return null;

  return (
    // Top-centre on mobile (the bottom is the tab bar); bottom-right on desktop,
    // clear of both the page heading and its action buttons.
    <div className="pointer-events-none fixed inset-x-0 top-3 z-90 flex flex-col items-center gap-2 px-4 lg:inset-x-auto lg:top-auto lg:right-5 lg:bottom-5 lg:items-end">
      {entries.map(([key, message]) => (
        <div
          key={key}
          role="status"
          className={clsx(
            "animate-scale-up border-line shadow-card pointer-events-auto flex w-full max-w-md items-start gap-3 border-2 px-4 py-3",
            key === "alert"
              ? "bg-sunset-orange text-white"
              : "bg-sidecar text-ink",
          )}
        >
          <div className="min-w-0 flex-1">
            <p className="font-display text-body font-bold">
              {key === "alert" ? "Oops…" : "Nice!"}
            </p>
            <p className="text-body">{message as string}</p>
          </div>

          <button
            type="button"
            aria-label="Dismiss"
            onClick={() => setDismissed((d) => [...d, key])}
            className="font-display text-subheading shrink-0 cursor-pointer leading-none font-bold opacity-70 transition-opacity hover:opacity-100"
          >
            ×
          </button>
        </div>
      ))}
    </div>
  );
}
